import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { staticPages, type StaticPage } from "@/mocks/admin";

/* ─── DB row → StaticPage mapper ─── */
function rowToPage(row: Record<string, unknown>): StaticPage {
  return {
    id: row.id as string,
    title: row.title as string,
    slug: row.slug as string,
    content: (row.content as string) || "",
    metaTitle: (row.meta_title as string) || "",
    metaDescription: (row.meta_description as string) || "",
    isPublished: (row.is_published as boolean) ?? true,
    lastEdited: (row.last_edited as string) || (row.updated_at as string) || new Date().toISOString(),
    sections: (row.sections as StaticPage["sections"]) || [],
  };
}

/* ─── StaticPage → DB row mapper ─── */
function pageToRow(page: Partial<StaticPage>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (page.title !== undefined) row.title = page.title;
  if (page.slug !== undefined) row.slug = page.slug;
  if (page.content !== undefined) row.content = page.content;
  if (page.metaTitle !== undefined) row.meta_title = page.metaTitle;
  if (page.metaDescription !== undefined) row.meta_description = page.metaDescription;
  if (page.isPublished !== undefined) row.is_published = page.isPublished;
  if (page.sections !== undefined) row.sections = page.sections;
  row.last_edited = new Date().toISOString();
  row.updated_at = new Date().toISOString();
  return row;
}

/* ─── usePagesDB — CRUD for CMS pages ─── */
export function usePagesDB() {
  const [pages, setPages] = useState<StaticPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from("pages")
      .select("*")
      .order("title", { ascending: true });

    if (dbError) {
      setError(dbError.message);
      // Fallback to mock data so admin never looks empty
      setPages(staticPages);
    } else if ((data ?? []).length === 0) {
      // No rows in DB yet — seed with mock data
      setPages(staticPages);
    } else {
      setPages((data ?? []).map(rowToPage));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const createPage = useCallback(async (
    page: Omit<StaticPage, "id" | "lastEdited">
  ): Promise<{ data: StaticPage | null; error: string | null }> => {
    const row = pageToRow(page);
    const { data, error: dbError } = await supabase
      .from("pages")
      .insert(row)
      .select()
      .maybeSingle();

    if (dbError) {
      // Fallback: create locally with mock ID
      const fallback: StaticPage = {
        ...page,
        id: `page-${Date.now()}`,
        lastEdited: new Date().toISOString(),
      };
      setPages((prev) => [...prev, fallback]);
      return { data: fallback, error: dbError.message };
    }
    if (data) {
      const newPage = rowToPage(data as Record<string, unknown>);
      await fetchPages();
      return { data: newPage, error: null };
    }
    return { data: null, error: "Unbekannter Fehler beim Erstellen." };
  }, [fetchPages]);

  const updatePage = useCallback(async (
    id: string,
    updates: Partial<StaticPage>
  ): Promise<{ error: string | null }> => {
    const row = pageToRow(updates);
    const { error: dbError } = await supabase
      .from("pages")
      .update(row)
      .eq("id", id);

    if (dbError) {
      // Fallback: update local state
      setPages((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                ...updates,
                lastEdited: new Date().toISOString(),
              }
            : p
        )
      );
      return { error: dbError.message };
    }
    await fetchPages();
    return { error: null };
  }, [fetchPages]);

  const deletePage = useCallback(async (id: string): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase
      .from("pages")
      .delete()
      .eq("id", id);

    if (dbError) {
      setPages((prev) => prev.filter((p) => p.id !== id));
      return { error: dbError.message };
    }
    await fetchPages();
    return { error: null };
  }, [fetchPages]);

  return {
    pages,
    loading,
    error,
    refetch: fetchPages,
    createPage,
    updatePage,
    deletePage,
  };
}

/* ─── usePageBySlug — fetch a single page for public pages ─── */
export function usePageBySlug(slug: string | undefined) {
  const [page, setPage] = useState<StaticPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }

    setLoading(true);
    setError(null);

    supabase
      .from("pages")
      .select("*")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle()
      .then(({ data, error: dbError }) => {
        if (dbError) {
          // Fallback to mock
          const fallback = staticPages.find((p) => p.slug === slug) || null;
          setPage(fallback);
          setError(dbError.message);
        } else if (data) {
          setPage(rowToPage(data as Record<string, unknown>));
        } else {
          // Not found in DB — try mock fallback
          const fallback = staticPages.find((p) => p.slug === slug) || null;
          setPage(fallback);
        }
        setLoading(false);
      });
  }, [slug]);

  return { page, loading, error };
}